import { styled, css } from "styled-components";
import { File } from "@ludens-reklame/rubics-v3-sdk/file/types";
import { mobileBreakPointCSS } from "../../defaults/theme.js";
import { RichText } from "../../internal-components/RichText.js";
import { FormField } from "./ContactForm.js";
import { Select } from "../../internal-components/Input/Select.js";
import { Input } from "../../internal-components/Input/Input.js";
import { CheckBox } from "../../internal-components/Input/Checkbox.js";
import { FILE_ENDPOINT } from "../../util/constants.js";
import { Uploader } from "../../internal-components/Input/Uploader.js";
import { ImageUploader } from "../../internal-components/Input/ImageUploader.js";
import { CheckboxGroup } from "../../internal-components/Input/CheckboxGroup.js";

export const FieldWrapper = styled.div<{ $short?: boolean }>`
  margin-bottom: ${(props) => props.theme.spacing.s}!important;
  clear: both;

  input {
    min-width: auto;
  }

  ${(props) =>
    props.$short &&
    css`
      @media (min-width: ${mobileBreakPointCSS}) {
        float: left;
        width: calc(50% - ${(props) => props.theme.spacing.m});
        clear: none;
      }
    `};

  @media (min-width: ${mobileBreakPointCSS}) {
    margin: calc(${(props) => props.theme.spacing.m} / 2);
  }
`;

interface RenderFieldOpts {
  field: FormField;
  data: any;
  setField: (name: string, value: any) => any;
  setUploadedImage?: React.Dispatch<React.SetStateAction<File | undefined>>;
  reactKey: number;
  site: string;
  isActionCorrespondence?: boolean;
}

export function renderField(opts: RenderFieldOpts): React.ReactNode {
  if (!opts.field.name) {
    return null;
  }

  if (opts.field.triggeredBy) {
    const trigger = opts.data[opts.field.triggeredBy];

    if (!trigger) {
      return null;
    }
  }

  let required: boolean = opts.field.required || false;

  if (opts.field.requirableBy && !required) {
    required = !!opts.data[opts.field.requirableBy];
  }

  let elem: React.ReactNode;
  const field = opts.field;
  const data = opts.data;
  const fieldName = field.name!;

  switch (field.type) {
    case "select":
      elem = (
        <Select
          label={field.label || ""}
          required={required}
          value={data[fieldName] || ""}
          onChange={(e) => opts.setField(fieldName, e.target.value)}
        >
          <option value="" disabled>
            Vennligst velg…
          </option>
          {(field.options || []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      );

      break;
    case "textarea":
      elem = (
        <Input
          textarea
          label={field.label || ""}
          placeholder={field.placeholder}
          required={required}
          onChange={(e) => opts.setField(fieldName, e.target.value)}
          value={data[fieldName]}
          maxLength={
            typeof field.limit === "number" && field.limit > 0
              ? field.limit
              : undefined
          }
          minLength={
            typeof field.min === "number" && field.min > 0
              ? field.min
              : undefined
          }
        />
      );

      break;
    case "checkbox":
      elem = (
        <CheckBox
          name={field.name}
          required={required}
          onChange={(e) => opts.setField(fieldName, e.target.checked)}
          hasRichLabel={!!field.richLabel}
          label={
            field.richLabel ? (
              <RichText dangerouslySetInnerHTML={{ __html: field.richLabel }} />
            ) : (
              field.label || ""
            )
          }
        />
      );

      break;
    case "file":
      elem = (
        <Uploader
          id={fieldName}
          required={required}
          label={field.label || ""}
          onUpload={(file) => {
            opts.setField(
              fieldName,
              `${FILE_ENDPOINT}/files/${file.Key}/download`
            );
          }}
          onDelete={() => opts.setField(fieldName, undefined)}
        />
      );
      break;
    case "image":
      elem = (
        <ImageUploader
          id={fieldName}
          name={field.name}
          siteName={opts.site}
          onUpload={(image) => {
            if (opts.isActionCorrespondence) {
              if (typeof opts.setUploadedImage !== "undefined") {
                opts.setUploadedImage(image);
              }
            } else {
              opts.setField(fieldName, image.url);
            }
          }}
          onDelete={() => {
            if (opts.isActionCorrespondence) {
              if (typeof opts.setUploadedImage !== "undefined") {
                opts.setUploadedImage(undefined);
              }
            } else {
              opts.setField(fieldName, undefined);
            }
          }}
          required={required}
          label={field.label}
        />
      );
      break;
    case "checkboxgroup":
      elem = (
        <CheckboxGroup
          label={field.label || ""}
          options={field.options || []}
          data={data}
          required={required}
          onChange={(fieldName, checked) => {
            opts.setField(fieldName, checked);
          }}
        />
      );
      break;
    default:
      elem = (
        <Input
          type={field.type || "text"}
          label={field.label || ""}
          placeholder={field.placeholder}
          required={required}
          onChange={(e) => opts.setField(fieldName, e.target.value)}
          value={data[fieldName]}
          maxLength={
            typeof field.limit === "number" && field.limit > 0
              ? field.limit
              : undefined
          }
          minLength={
            typeof field.min === "number" && field.min > 0
              ? field.min
              : undefined
          }
        />
      );
  }

  return (
    <FieldWrapper
      key={opts.reactKey}
      className="field-wrapper"
      $short={field.short}
    >
      {elem}
    </FieldWrapper>
  );
}
